/**
 * The story of a section: how it was written and what has changed since, in
 * plain sentences grouped into six short chapters.
 *
 * Built ONLY from the recorded action history — the tool calls, the draft
 * event and the activity events — with a fixed wording per kind of step. No
 * model writes it, so the story can never claim a step that did not happen;
 * the agent's own words appear only as its quoted rationale on a calculation.
 *
 * Chapters, in order:
 *   1. aim      — what it set out to do (the attempt's steering, the plan)
 *   2. read     — what it read: each passage by document (with the parts of
 *                 the text that use it), library searches, literature results
 *   3. worked   — what it worked out (calculate / create_artefact, with derivation)
 *   4. wrote    — what it wrote: a summary, the gaps it left, and the text
 *                 part by part with the passages each part matches
 *   5. checked  — what was checked (hand-in outcomes, failed steps)
 *   6. changed  — every edit since, newest first: who, when, what kind
 *
 * Pure and shared by the web app and the Word add-in, so both tell the same
 * story from the same record.
 */

import {
  type ActionDerivation,
  type ActionHistoryPayload,
  type ActivityEvent,
  activityLabel,
  type DraftEvent,
  type GenericToolEvent,
  type InternetEvent,
  type LibraryCall,
  type RetrievalEvent,
  submitProgressLabel,
  type TimelineEvent,
  type WrittenPart,
  type WrittenRecord,
} from './actionHistory'

export type StoryChapterKey = 'aim' | 'read' | 'worked' | 'wrote' | 'checked' | 'changed'

/** One row under a story line: a passage read, a part of the text, a web result. */
export interface StoryItem {
  key: string
  /** Short label in the margin: "9.2", "¶2", "Table 1", "PubMed". */
  label?: string
  text: string
  /** The text is quoted, from a source or from the section. */
  quote?: boolean
  /** A quiet line under it: where it went, or where it came from. */
  detail?: string
  /** Shown when the row is opened: the excerpt read, or what a part shares with its sources. */
  more?: string
  attention?: boolean
  /** A heading of the section, shown as a divider in the outline. */
  heading?: boolean
}

export interface StoryLine {
  /** Stable within one story, for React keys and open state. */
  key: string
  /** One plain sentence. */
  text: string
  /** A quieter second line: where, or the quoted text. */
  detail?: string
  /** When it happened (ISO), for the edits chapter. */
  at?: string | null
  /** Who, for the edits chapter. */
  actor?: string
  /** Something a reviewer should look at (a failed step, an unverified input). */
  attention?: boolean
  /** A calculation's card. */
  derivation?: ActionDerivation
  /** The record the line came from, for "All steps". */
  event?: TimelineEvent
  /** The line in detail: one row per passage, part or result. */
  items?: StoryItem[]
}

export interface StoryChapter {
  key: StoryChapterKey
  title: string
  lines: StoryLine[]
}

export interface StoryFact {
  value: string
  label: string
  attention?: boolean
}

export interface SectionStory {
  /** "Drafted by the assistant · read 7 passages in 3 documents · wrote 420 words · …" */
  headline: string
  /** The headline as figures: passages read, words written, sources matched, edits. */
  facts: StoryFact[]
  /** The whole story as a few short paragraphs of plain narrative — the Story tab. */
  summary: string[]
  chapters: StoryChapter[]
}

const CHAPTER_TITLE: Record<StoryChapterKey, string> = {
  aim: 'What it set out to do',
  read: 'What it read',
  worked: 'What it worked out',
  wrote: 'What it wrote',
  checked: 'What was checked',
  changed: 'What changed since',
}

const DERIVED_TOOLS = new Set(['calculate', 'create_artefact'])
const SUBMIT_TOOLS = new Set(['submit_draft', 'submit_rewrite'])

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

const COUNT_WORD = ['no', 'once', 'twice']
function times(n: number): string {
  return n < COUNT_WORD.length ? (COUNT_WORD[n] as string) : `${n} times`
}

/** The document a library read was of, when its params or source name one. */
export function storyDocName(call: Pick<LibraryCall, 'input_params' | 'source_detail'>): string | null {
  const params = call.input_params ?? {}
  const ref = call.source_detail?.source_ref ?? {}
  for (const value of [
    params.doc_name,
    params.file_label,
    params.document,
    ref.filename,
    ref.doc_name,
    ref.file_label,
    ref.document,
    ref.title,
  ]) {
    const s = str(value)
    if (s !== null) return s
  }
  return null
}

/** The part of the document a read looked at: a section, a table, a page. */
function partOf(call: RetrievalEvent): string | null {
  const p = call.input_params ?? {}
  const part = str(p.section_label) ?? str(p.table_label) ?? str(p.figure_label) ?? str(call.source_detail?.section_number)
  if (part !== null) return part
  const page = typeof p.page_no === 'number' ? p.page_no : typeof p.page === 'number' ? p.page : null
  return page !== null ? `p.${page}` : null
}

function isSearch(call: RetrievalEvent): boolean {
  return str(call.input_params?.query) !== null && storyDocName(call) === null
}

function operationWords(operation: string): string {
  return operation.replace(/_/g, ' ').trim()
}


/** Plain, single-line, at most ``max`` characters, cut at a word. */
function clip(text: string, max: number): string {
  const plain = text.replace(/[#*_`|>]+/g, ' ').replace(/\s+/g, ' ').trim()
  if (plain.length <= max) return plain
  const cut = plain.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${space > max * 0.6 ? cut.slice(0, space) : cut}…`
}

/** "¶3", "Table 1", "List 2" — how a part of the text is referred to everywhere in the story. */
function partLabels(parts: readonly WrittenPart[]): Map<number, string> {
  const labels = new Map<number, string>()
  let para = 0
  let table = 0
  for (const part of parts) {
    if (part.kind === 'heading') continue
    if (part.kind === 'table') labels.set(part.index, `Table ${String(++table)}`)
    else labels.set(part.index, `¶${String(++para)}`)
  }
  return labels
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} and ${labels.at(-1) as string}`
}

/** "Protocol v3.0 · 9.2" for a read, by its transaction. */
function readName(read: RetrievalEvent | undefined, fallbackSection: string | null): string {
  if (!read) return fallbackSection ? `a passage (${fallbackSection})` : 'a passage it read'
  const doc = storyDocName(read) ?? 'a library document'
  const part = partOf(read) ?? fallbackSection
  return part ? `${doc} · ${part}` : doc
}

/** "Calculated sample size 2x2 crossover = 28 subjects from cv_percent 24.6 (Pilot study report)". */
function workedLine(ev: GenericToolEvent, index: number): StoryLine {
  const d = ev.derivation ?? null
  const failed = ev.outcome === 'failure'
  if (d === null) {
    const op = str(ev.input_params?.operation) ?? str(ev.input_params?.save_as)
    const what = ev.tool_name === 'create_artefact' ? 'Built a table' : 'Calculated'
    return {
      key: `worked-${index}`,
      text: `${what}${op ? `: ${operationWords(op)}` : ''}${failed ? ' (failed)' : ''}`,
      detail: failed ? (str(ev.error) ?? undefined) : undefined,
      attention: failed,
      event: ev,
    }
  }
  const output = d.value ? `${d.value} ${d.unit}`.trim() : d.matched_values.join(', ')
  const sourced = d.inputs.filter((i) => i.kind !== 'design' && i.kind !== 'unsourced' && i.kind !== 'literal')
  const from = sourced
    .slice(0, 3)
    .map((i) => {
      const where = i.citation?.document_name || i.source
      const value = i.value === undefined || i.value === null || typeof i.value === 'object' ? '' : ` ${String(i.value)}`
      return `${i.name}${value}${where ? ` (${where})` : ''}`
    })
    .join(', ')
  const design = d.inputs.filter((i) => i.kind === 'design').map((i) => i.name)
  const text =
    d.kind === 'artefact'
      ? `Built a table from ${plural(d.inputs.length, 'source table')}${from ? `: ${from}` : ''}`
      : `Calculated ${operationWords(d.operation)}${output ? ` = ${output}` : ''}${from ? ` from ${from}` : ''}`
  return {
    key: `worked-${index}`,
    text,
    detail: design.length > 0 ? `Design choices: ${design.join(', ')}` : undefined,
    attention: !d.verified,
    derivation: d,
    event: ev,
  }
}

function editLine(ev: ActivityEvent, index: number): StoryLine {
  const { label, sub } = activityLabel(ev)
  const instruction = str(ev.detail?.instruction)
  const actor = ev.actor_type === 'ai_agent' ? 'the assistant' : ev.actor || 'Someone'
  return {
    key: `changed-${index}`,
    text: instruction ? `Edited on request: “${instruction}”` : label,
    detail: sub && /^v\d+$/.test(sub) ? `Version ${sub.slice(1)}` : sub,
    at: ev.started_at,
    actor,
    attention: ev.outcome === 'error' || ev.outcome === 'conflict',
    event: ev,
  }
}


/** "A", "A and B", "A, B and C". */
function listWords(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1) as string}`
}

function sectionsWords(parts: readonly string[]): string {
  if (parts.length === 0) return ''
  const shown = parts.slice(0, 4)
  const more = parts.length > shown.length ? ` and ${String(parts.length - shown.length)} more` : ''
  return ` (${parts.length === 1 ? 'section' : 'sections'} ${listWords(shown)}${more})`
}

/** The story of one section's record. */
export function buildSectionStory(payload: ActionHistoryPayload): SectionStory {
  const timeline = [...(payload.timeline ?? [])].sort((a, b) =>
    String(a.started_at ?? '').localeCompare(String(b.started_at ?? '')),
  )
  const reads = timeline.filter((e): e is RetrievalEvent => e.kind === 'retrieval')
  const web = timeline.filter((e): e is InternetEvent => e.kind === 'internet')
  const tools = timeline.filter((e): e is GenericToolEvent => e.kind === 'tool')
  const drafts = timeline.filter((e): e is DraftEvent => e.kind === 'draft')
  const activity = timeline.filter((e): e is ActivityEvent => e.kind === 'activity')

  // 1. Aim — the attempt's steering, and the plan when there was one.
  const aim: StoryLine[] = []
  const attempt = (payload.attempts ?? []).find((a) =>
    payload.attempt != null ? a.attempt_number === payload.attempt : a.is_current,
  )
  if (attempt?.guidance) aim.push({ key: 'aim-guidance', text: `Steering given: “${attempt.guidance}”` })
  else aim.push({ key: 'aim-instructions', text: 'Drafted from the section’s instructions and the doctype’s guidance.' })
  if ((payload.attempts ?? []).length > 1 && attempt) {
    aim.push({ key: 'aim-attempt', text: `This is attempt ${attempt.attempt_number} of ${payload.attempts?.length}.` })
  }
  const plan = payload.plan_nodes ?? []
  if (plan.length > 1) aim.push({ key: 'aim-plan', text: `Split the work into ${plural(plan.length, 'part')}.` })

  // 2. Read — by document, one row per passage; then searches. When the
  // written text came with the record, each passage says which parts of the
  // text match it, or that none does.
  const written: WrittenRecord | null = payload.written && payload.written.parts.length > 0 ? payload.written : null
  const labels = partLabels(written?.parts ?? [])
  const usedIn = new Map<string, string[]>()
  const citedIn = new Map<string, string[]>()
  for (const part of written?.parts ?? []) {
    const label = labels.get(part.index)
    if (!label) continue
    for (const src of part.sources ?? []) usedIn.set(src.transaction_id, [...(usedIn.get(src.transaction_id) ?? []), label])
    for (const key of part.cite_keys ?? []) citedIn.set(key, [...(citedIn.get(key) ?? []), label])
  }
  const readByTx = new Map<string, RetrievalEvent>()
  for (const r of reads) if (r.transaction_id) readByTx.set(r.transaction_id, r)

  const read: StoryLine[] = []
  const byDoc = new Map<string, RetrievalEvent[]>()
  const searches: RetrievalEvent[] = []
  for (const r of reads) {
    if (isSearch(r)) {
      searches.push(r)
      continue
    }
    const doc = storyDocName(r) ?? 'A library document'
    byDoc.set(doc, [...(byDoc.get(doc) ?? []), r])
  }
  let i = 0
  for (const [doc, passages] of byDoc) {
    const used = passages.filter((r) => r.transaction_id && usedIn.has(r.transaction_id)).length
    read.push({
      key: `read-${String(i++)}`,
      text: `${doc}: ${plural(passages.length, 'passage')}`,
      detail: written
        ? used === 0
          ? 'No part of the text matches what it read here.'
          : `${String(used)} of ${String(passages.length)} used in the text.`
        : undefined,
      event: passages[0],
      items: passages.map((r, n) => {
        const used = (r.transaction_id && usedIn.get(r.transaction_id)) || []
        const snippet = r.source_detail?.snippet ?? ''
        const size = r.source_detail?.content_length || r.result_length
        const meta = [
          size > 0 ? `${size.toLocaleString('en-US')} characters` : null,
          r.count > 1 ? `read ${times(r.count)}` : null,
          r.outcome === 'failure' ? 'failed' : null,
          written ? (used.length > 0 ? `used in ${joinLabels(used)}` : 'read for context, not matched in the text') : null,
        ].filter(Boolean)
        return {
          key: `read-${String(i)}-${String(n)}`,
          label: partOf(r) ?? undefined,
          text: snippet.trim() ? clip(snippet, 160) : 'No text was kept for this read.',
          quote: snippet.trim().length > 0,
          detail: meta.join(' · ') || undefined,
          more: snippet.trim().length > 160 ? clip(snippet, 1200) : undefined,
          attention: r.outcome === 'failure',
        }
      }),
    })
  }
  if (searches.length > 0) {
    const n = searches.reduce((sum, r) => sum + Math.max(1, r.count), 0)
    read.push({
      key: 'read-search',
      text: `Searched your library ${times(n)}`,
      items: searches.map((r, n) => ({
        key: `read-search-${String(n)}`,
        text: `“${str(r.input_params?.query) ?? ''}”`,
        detail: [
          r.result_length > 0 ? `${r.result_length.toLocaleString('en-US')} characters back` : 'nothing back',
          r.count > 1 ? `searched ${times(r.count)}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    })
  }
  for (const [n, w] of web.entries()) {
    const results = w.web_results ?? []
    const topic = str(w.goal) ?? str(w.query)
    const cited = results.filter((res) => res.citation_key && citedIn.has(res.citation_key)).length
    read.push({
      key: `read-web-${String(n)}`,
      text: `Searched the literature${topic ? ` for “${topic}”` : ''}: ${plural(results.length, 'result')}`,
      detail: written && results.length > 0 ? `${String(cited)} of ${String(results.length)} cited in the text.` : undefined,
      event: w,
      items: results.slice(0, 8).map((res, k) => {
        const where = (res.citation_key && citedIn.get(res.citation_key)) || []
        return {
          key: `read-web-${String(n)}-${String(k)}`,
          label: res.source === 'clinical_trials' ? 'Trials' : res.source === 'pubmed' ? 'PubMed' : res.source,
          text: `${res.title}${res.year ? ` (${String(res.year)})` : ''}`,
          detail: [res.journal, written ? (where.length > 0 ? `cited in ${joinLabels(where)}` : 'not cited') : null]
            .filter(Boolean)
            .join(' · ') || undefined,
          more: res.abstract ? clip(res.abstract, 600) : undefined,
        }
      }),
    })
  }

  // 3. Worked out.
  const worked = tools.filter((t) => DERIVED_TOOLS.has(t.tool_name)).map(workedLine)

  // 4. Wrote — a summary of the text, its gaps, and the text part by part
  // with the passages each part matches.
  const wrote: StoryLine[] = []
  const draft = drafts.at(-1)
  if (written) {
    const body = written.parts.filter((p) => p.kind !== 'heading')
    const count = (kind: WrittenPart['kind'], one: string) => {
      const n = body.filter((p) => p.kind === kind).length
      return n > 0 ? plural(n, one) : null
    }
    const headings = written.parts.length - body.length
    const shape = [count('paragraph', 'paragraph'), count('list', 'list'), count('table', 'table')].filter(Boolean)
    const matched = body.filter((p) => (p.sources ?? []).length > 0).length
    wrote.push({
      key: 'wrote-summary',
      text: `Wrote ${written.words.toLocaleString('en-US')} words in ${joinLabels(shape as string[])}${
        headings > 0 ? ` under ${plural(headings, 'heading')}` : ''
      }.`,
      detail:
        matched === body.length
          ? 'Every part matches a passage it read.'
          : `${String(matched)} of ${plural(body.length, 'part')} match a passage it read; ${String(body.length - matched)} ${
              body.length - matched === 1 ? 'matches' : 'match'
            } none.`,
      event: draft,
    })
    const gaps = body.flatMap((p) => (p.markers ?? []).map((m) => ({ part: p, marker: m })))
    if (gaps.length > 0) {
      wrote.push({
        key: 'wrote-gaps',
        text: `Left ${plural(gaps.length, 'gap')} for you to fill.`,
        attention: true,
        items: gaps.map((g, n) => ({
          key: `wrote-gap-${String(n)}`,
          label: labels.get(g.part.index),
          text: g.marker.replace(/^\[|\]$/g, ''),
          attention: true,
        })),
      })
    }
    wrote.push({
      key: 'wrote-outline',
      text: 'Part by part',
      items: written.parts.map((part) => {
        if (part.kind === 'heading') return { key: `wrote-part-${String(part.index)}`, text: part.opening, heading: true }
        const sources = part.sources ?? []
        const named = sources.map((src) => readName(readByTx.get(src.transaction_id), src.section_number))
        const size = part.kind === 'table' ? plural(part.rows ?? 0, 'row') : plural(part.words, 'word')
        return {
          key: `wrote-part-${String(part.index)}`,
          label: labels.get(part.index),
          text: part.opening,
          quote: part.kind !== 'table',
          detail: [
            size,
            named.length > 0 ? `from ${named.join('; ')}` : 'matches no passage it read',
            (part.markers ?? []).length > 0 ? plural((part.markers ?? []).length, 'gap') + ' left' : null,
          ]
            .filter(Boolean)
            .join(' · '),
          more:
            sources.filter((src) => src.shared_numbers.length > 0).length > 0
              ? sources
                  .filter((src) => src.shared_numbers.length > 0)
                  .map(
                    (src) =>
                      `Shares ${src.shared_numbers.join(', ')} with ${readName(readByTx.get(src.transaction_id), src.section_number)}`,
                  )
                  .join('\n')
              : undefined,
        }
      }),
    })
  } else if (draft) {
    wrote.push({
      key: 'wrote-draft',
      text: draft.draft_length > 0 ? `Wrote the section (${draft.draft_length.toLocaleString('en-US')} characters).` : 'Wrote the section.',
      event: draft,
    })
  }
  const submits = tools.filter((t) => SUBMIT_TOOLS.has(t.tool_name))
  const lastSubmit = submits.at(-1)
  if (lastSubmit?.submit_progress) {
    const { label, sub } = submitProgressLabel(lastSubmit.submit_progress)
    wrote.push({ key: 'wrote-submit', text: label, detail: sub, event: lastSubmit })
  }

  // 5. Checked — hand-in outcomes and steps that failed.
  const checked: StoryLine[] = []
  const sentBack = submits.filter((t) => t.submit_progress && t.submit_progress.outcome && t.submit_progress.outcome !== 'accepted')
  if (sentBack.length > 0) {
    checked.push({
      key: 'checked-sentback',
      text: `The hand-in check sent the draft back ${times(sentBack.length)} before it was accepted.`,
      detail: sentBack.map((s) => str(s.error)).filter(Boolean).slice(0, 2).join(' · ') || undefined,
    })
  } else if (submits.length > 0) {
    checked.push({ key: 'checked-ok', text: 'Passed the hand-in check first time.' })
  }
  const failed = [...reads, ...tools].filter((t) => t.outcome === 'failure')
  if (failed.length > 0) {
    checked.push({
      key: 'checked-failed',
      text: `${plural(failed.length, 'step')} failed and ${failed.length === 1 ? 'was' : 'were'} worked around.`,
      detail: failed.map((f) => str(f.error)).filter(Boolean).slice(0, 2).join(' · ') || undefined,
      attention: true,
    })
  }
  // Only a calculation with a recorded derivation can be said to be traced;
  // one without says so rather than being counted as checked.
  const unverified = worked.filter((w) => w.derivation && !w.derivation.verified).length
  const untraced = worked.filter((w) => !w.derivation).length
  if (unverified > 0) {
    checked.push({
      key: 'checked-inputs',
      text: `${plural(unverified, 'calculation')} used an input not found in its source.`,
      attention: true,
    })
  }
  if (worked.length > 0 && unverified === 0 && untraced === 0) {
    checked.push({ key: 'checked-inputs', text: 'Every number it worked out was traced to its sources.' })
  } else if (untraced > 0) {
    checked.push({
      key: 'checked-untraced',
      text:
        untraced === worked.length
          ? 'No record of the inputs was kept for what it worked out.'
          : `${String(worked.length - untraced)} of ${String(worked.length)} figures it worked out ${worked.length - untraced === 1 ? 'was' : 'were'} traced to their sources; the rest have no record of their inputs.`,
    })
  }

  // 6. Changed since — newest first; the agent's own first draft is not an edit.
  const reviews = activity.filter((a) => !a.action.startsWith('section.'))
  const edits = activity
    .filter(
      (a) =>
        a.action.startsWith('section.') &&
        a.action !== 'section.agent_draft' &&
        a.action !== 'section.agent_draft_authority',
    )
    .map(editLine)
    .reverse()
  if (reviews.length > 0) {
    checked.unshift({
      key: 'checked-review',
      text: `Reviewed ${times(reviews.length)} after drafting; any issues found are on the Issues tab.`,
    })
  }

  const chapters: StoryChapter[] = (
    [
      ['aim', aim],
      ['read', read],
      ['worked', worked],
      ['wrote', wrote],
      ['checked', checked],
      ['changed', edits],
    ] as const
  )
    .filter(([key, lines]) => lines.length > 0 || key === 'changed')
    .map(([key, lines]) => ({ key, title: CHAPTER_TITLE[key], lines: [...lines] }))

  const passages = [...byDoc.values()].reduce((sum, list) => sum + list.length, 0)
  const headline = [
    draft || tools.length + reads.length > 0 ? 'Drafted by the assistant' : 'Not drafted yet',
    passages > 0 ? `read ${plural(passages, 'passage')} in ${plural(byDoc.size, 'document')}` : null,
    written ? `wrote ${written.words.toLocaleString('en-US')} words` : null,
    worked.length > 0 ? `worked out ${plural(worked.length, 'figure')}` : null,
    edits.length > 0 ? `edited ${times(edits.length)} since` : 'not edited since',
  ]
    .filter(Boolean)
    .join(' · ')

  const body = (written?.parts ?? []).filter((p) => p.kind !== 'heading')
  const gapCount = body.reduce((sum, p) => sum + (p.markers ?? []).length, 0)
  const facts: StoryFact[] = [
    { value: String(passages), label: passages === 1 ? 'passage read' : 'passages read' },
    written
      ? { value: written.words.toLocaleString('en-US'), label: 'words written' }
      : { value: String(draft?.draft_length ?? 0), label: 'characters written' },
    ...(written
      ? [
          {
            value: `${String(body.filter((p) => (p.sources ?? []).length > 0).length)}/${String(body.length)}`,
            label: 'parts match a source',
          },
        ]
      : []),
    ...(gapCount > 0 ? [{ value: String(gapCount), label: gapCount === 1 ? 'gap left' : 'gaps left', attention: true }] : []),
    { value: String(edits.length), label: edits.length === 1 ? 'edit since' : 'edits since' },
  ]

  // The narrative: the same record in a few paragraphs, fixed wording only.
  const summary: string[] = []
  const docWords = [...byDoc.entries()].map(([doc, list]) => {
    const parts = [...new Set(list.map((r) => partOf(r)).filter((p): p is string => p !== null))]
    return `${doc}${sectionsWords(parts)}`
  })
  const sourcing: string[] = []
  if (docWords.length > 0) {
    sourcing.push(
      `The assistant drafted this section from ${plural(docWords.length, 'document')}: ${listWords(docWords)}.`,
    )
  } else if (draft || tools.length > 0) {
    sourcing.push('The assistant drafted this section without reading a library document.')
  }
  if (searches.length > 0) {
    const n = searches.reduce((sum, r) => sum + Math.max(1, r.count), 0)
    sourcing.push(`It searched your library ${times(n)} to find them.`)
  }
  for (const w of web) {
    const results = w.web_results ?? []
    const topic = str(w.goal) ?? str(w.query)
    const cited = results.filter((res) => res.citation_key && citedIn.has(res.citation_key)).length
    sourcing.push(
      `It searched the published literature${topic ? ` for “${topic}”` : ''} and found ${plural(results.length, 'result')}${
        !written || results.length === 0
          ? ''
          : results.length === 1
            ? cited === 1
              ? ', which is cited in the text'
              : ', which is not cited in the text'
            : `, ${cited === 0 ? 'none' : String(cited)} of which ${cited === 1 ? 'is' : 'are'} cited in the text`
      }.`,
    )
  }
  if (sourcing.length > 0) summary.push(sourcing.join(' '))

  const workedWords = tools
    .filter((t) => DERIVED_TOOLS.has(t.tool_name) && t.outcome !== 'failure')
    // Figures first, tables last, so the list reads "rate (…), percentage and a table".
    .sort((a, b) => Number(a.tool_name === 'create_artefact') - Number(b.tool_name === 'create_artefact'))
    .map((t) => {
      const d = t.derivation
      if (t.tool_name === 'create_artefact') return d ? `a table built from ${plural(d.inputs.length, 'source table')}` : 'a table'
      const op = d?.operation ?? str(t.input_params?.operation)
      const value = d?.value ? ` (${`${d.value} ${d.unit}`.trim()})` : ''
      return `${op ? operationWords(op) : 'a figure'}${value}`
    })
  if (workedWords.length > 0) {
    const traced = worked.filter((w) => w.derivation && w.derivation.verified).length
    summary.push(
      `It used the built-in calculator for ${plural(workedWords.length, 'figure')}: ${listWords(workedWords)}. ${
        traced === worked.length
          ? `${workedWords.length === 1 ? 'Its inputs were' : 'Every input to them was'} checked against the source.`
          : `${String(traced)} of ${String(worked.length)} ${traced === 1 ? 'has its' : 'have their'} inputs traced to a source; open one below to check it.`
      }`,
    )
  }

  if (written) {
    const bodyParts = written.parts.filter((p) => p.kind !== 'heading')
    const tables = bodyParts.filter((p) => p.kind === 'table').length
    const paras = bodyParts.length - tables
    const unmatched = bodyParts.filter((p) => (p.sources ?? []).length === 0).map((p) => labels.get(p.index) ?? '')
    const gaps = bodyParts.flatMap((p) => p.markers ?? []).map((m) => m.replace(/^\[[A-Z ]+:?\s*|\]$/g, '').trim())
    const shape = [paras > 0 ? plural(paras, 'paragraph') : null, tables > 0 ? plural(tables, 'table') : null].filter(
      (x): x is string => x !== null,
    )
    const writing = [`It wrote ${written.words.toLocaleString('en-US')} words in ${listWords(shape)}.`]
    writing.push(
      unmatched.length === 0
        ? 'Every part of the text matches a passage it read.'
        : `${listWords(unmatched)} ${unmatched.length === 1 ? 'matches' : 'match'} no passage it read, so check ${
            unmatched.length === 1 ? 'it' : 'them'
          } first.`,
    )
    if (gaps.length > 0) {
      writing.push(`It left ${plural(gaps.length, 'gap')} for you to fill: ${listWords(gaps.slice(0, 3))}.`)
    }
    summary.push(writing.join(' '))
  } else if (draft && draft.draft_length > 0) {
    summary.push(`It wrote ${draft.draft_length.toLocaleString('en-US')} characters.`)
  }

  const after: string[] = []
  if (sentBack.length > 0) after.push(`The hand-in check sent it back ${times(sentBack.length)} before accepting it.`)
  else if (submits.length > 0) after.push('It passed the hand-in check first time.')
  if (failed.length > 0) after.push(`${plural(failed.length, 'step')} failed and ${failed.length === 1 ? 'was' : 'were'} worked around.`)
  if (reviews.length > 0) after.push(`The reviewer checked it ${times(reviews.length)}; anything it raised is on the Issues tab.`)
  const last = edits[0]
  after.push(
    edits.length === 0
      ? 'Nobody has edited it since.'
      : `It has been edited ${times(edits.length)} since${last?.actor ? `, most recently by ${last.actor}` : ''}.`,
  )
  summary.push(after.join(' '))

  return { headline, facts, summary, chapters }
}
